/**
 * Client-side image compression helper.
 *
 * Before any avatar / photo / ID upload we squeeze the image down to ≤1 MB and
 * ≤1920 px on the longest edge. Browsers running on phones routinely produce
 * 10–15 MB shots and the server then has to decode + transcode HEIC, which is
 * slow and sometimes times out on bad mobile data. Shrinking on-device fixes
 * both at no quality loss the user would notice.
 *
 * HEIC files are left untouched — the backend's pillow_heif transcoder needs
 * the original bytes to convert to JPG.
 */
import imageCompression from "browser-image-compression";

const DEFAULT_OPTIONS = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  initialQuality: 0.85,
  useWebWorker: true,
  // Preserve the original mime type where possible (jpeg/png).
};

const SKIP_TYPES = new Set([
  "image/heic",
  "image/heif",
  "image/gif", // animation — leave alone
  "image/svg+xml",
]);

export async function compressImage(file, overrides = {}) {
  if (!file || !(file instanceof File) && !(file instanceof Blob)) return file;

  // Tiny files don't need touching
  if (file.size < 600 * 1024) return file;

  const type = file.type || "";
  if (SKIP_TYPES.has(type)) return file;
  // Also skip if the extension is HEIC (some browsers report empty type)
  if (file.name && /\.heic$|\.heif$/i.test(file.name)) return file;

  try {
    const compressed = await imageCompression(file, { ...DEFAULT_OPTIONS, ...overrides });
    // imageCompression returns a Blob; wrap as File to keep .name + .lastModified
    if (compressed instanceof File) return compressed;
    return new File([compressed], file.name || "upload.jpg", {
      type: compressed.type || type || "image/jpeg",
      lastModified: Date.now(),
    });
  } catch (err) {
    // If anything goes wrong, fall back to the original — the upload still works,
    // it'll just be slower.
    // eslint-disable-next-line no-console
    console.warn("[compressImage] fallback to original:", err);
    return file;
  }
}

export default compressImage;
